import { ListSkeleton } from 'components/Skeleton'
import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { exposeStrapiError } from 'utils'

import UserApprovalsTable from 'components/UserApprovalsTable'
import {
  ApprovePendingAccess,
  ReadPendingAccess,
  ReadPendingIdentityReview,
  ReadPendingAccessCompanies,
  RejectPendingAccess,
} from 'services/approval'
import { Icon } from 'ui/styled'

import {
  FootNote,
  HeaderMeta,
  HeaderRow,
  HeaderSubtitle,
  HeaderTitle,
  Hero,
  Page,
  Panel,
  PanelInner,
  Shell,
  StateCard,
  StateText,
  StateTitle,
} from './styled'
import ContainerUnauthenticated from 'containers/Unauthenticated'

const getErrorMessage = (status, message) => {
  if (status === 401) return message || 'This approval link is invalid or expired.'
  if (status === 403) return message || 'You do not have access to these approval requests.'
  if (status === 404) return message || 'This approval page could not be found.'
  if (status === 0) return message || 'Network error. Please try again.'
  return message || 'Unable to load the approval page right now.'
}

export default function Approval() {
  const { token } = useParams()

  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [rowAction, setRowAction] = useState({})
  const [rowDecision, setRowDecision] = useState({})

  const loadSummary = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)

    const result = await ReadPendingAccess(token)

    if (!result?.ok) {
      setSummary(null)
      setErrorMessage(getErrorMessage(result?.status, result?.data?.message))
      if (!silent) setLoading(false)
      return
    }

    setSummary(result?.data || null)
    setErrorMessage('')
    if (!silent) setLoading(false)
  }, [token])

  useEffect(() => {
    setRowAction({})
    setRowDecision({})
    loadSummary()
  }, [loadSummary])

  const handleDecision = useCallback(async (userId, action, options = {}) => {
    setRowAction((prev) => ({ ...prev, [userId]: action }))

    const result = action === 'approve'
      ? await ApprovePendingAccess(token, userId, options.companyId, options.useRegistrationCompany, options)
      : await RejectPendingAccess(token, userId)

    if (!result?.ok && result?.data?.code === 'salesforce_review_required') {
      setRowAction(previous => { const next = { ...previous }; delete next[userId]; return next })
      return { reviewRequired: true }
    }
    if (!result?.ok) {
      const status = result?.status
      const message = result?.data?.message

      if (status === 401 || status === 403) {
        setSummary(null)
        setErrorMessage(getErrorMessage(status, message))
      } else if (!exposeStrapiError(result?.data)) {
        toast.error(`Unable to ${action} this user.`)
      }
    } else {
      setRowDecision((prev) => ({ ...prev, [userId]: action }))
      toast.success(result?.data?.message || `User ${action}d successfully.`)
    }

    setRowAction((prev) => {
      const next = { ...prev }
      delete next[userId]
      return next
    })
    return !!result?.ok
  }, [token])

  const loadCompanyOptions = useCallback((params) => (
    ReadPendingAccessCompanies(token, params)
  ), [token])
  const loadIdentityReview = useCallback((id, companyName, accountId) => ReadPendingIdentityReview(token, id, companyName, accountId), [token])

  const rows = summary?.rows || []
  const showStateColumn = !!summary?.showStateColumn
  const showCepColumn = !!summary?.showCepColumn
  const totalPending = rows.filter((row) => !rowDecision?.[row?.id]).length

  return (
    <ContainerUnauthenticated success>
      <Page>
        <Shell>
          <Hero>
            <Icon icon="MV_Universe_Logo_white" width={280} nomargin />
          </Hero>

          <Panel>
            <PanelInner>
              {loading ? (
                <ListSkeleton rows={7} />
              ) : errorMessage ? (
                <StateCard>
                  <StateTitle>Unable to open approval page</StateTitle>
                  <StateText>{errorMessage}</StateText>
                </StateCard>
              ) : (
                <>
                  <HeaderRow>
                    <div>
                      <HeaderTitle>Daily access approvals</HeaderTitle>
                      <HeaderSubtitle>pending users list</HeaderSubtitle>
                    </div>
                    <HeaderMeta>
                      <strong>Total:</strong> {totalPending}
                      {' '}|{' '}
                      <strong>Generated:</strong> {summary?.generatedAt || '-'}
                    </HeaderMeta>
                  </HeaderRow>

                  {!rows.length ? (
                    <StateCard compact>
                      <StateTitle>No pending users</StateTitle>
                      <StateText>There are no pending access requests for this approval scope right now.</StateText>
                    </StateCard>
                  ) : (
                    <UserApprovalsTable
                      rows={rows}
                      rowAction={rowAction}
                      rowDecision={rowDecision}
                      showStateColumn={showStateColumn}
                      showCepColumn={showCepColumn}
                      onDecision={handleDecision}
                      loadCompanyOptions={loadCompanyOptions}
                      loadIdentityReview={loadIdentityReview}
                    />
                  )}

                  <FootNote>
                    If you do not recognize any request, you can safely ignore this page.
                  </FootNote>
                </>
              )}
            </PanelInner>
          </Panel>
        </Shell>
      </Page>
    </ContainerUnauthenticated>
  )
}
